import React, { useState } from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from 'components/mui';
import { DeleteIcon, EditIcon } from 'components/icons';
import { Button, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CaseCommentEditor from '../editor/CaseCommentEditor';
import { CANtrackComment } from '../../types/comment';
import useCommentEditor from '../../hooks/useCommentEditor';
import DeleteCommentWarning from '../alerts/DeleteCommentWarning';
import CommentAttachments from '../attachments/CommentAttachments';

const CaseCommentsTableRowExpanded: React.FC<{
	comment: CANtrackComment;
	isEditing: boolean;
	setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
}> = ({ comment, isEditing, setIsEditing, setComments }) => {
	const theme = useTheme();
	const [showDeleteConfirmation, setShowDeleteConfirmation] =
		useState<boolean>(false);

	const {
		errors,
		formState,
		handleChange,
		handleDeleteComment,
		handleSaveComment,
		handleResetComment,
	} = useCommentEditor(setComments, comment);

	const handleConfirmEdit = async () => {
		await handleSaveComment();
		setIsEditing(false);
	};

	return (
		<TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
			<TableCell
				colSpan={8}
				sx={{ p: { xs: 1, md: 2 } }}
			>
				<Box sx={{ width: '100%' }}>
					{isEditing ? (
						<CaseCommentEditor
							comment={comment}
							errors={errors}
							formState={formState}
							handleChange={handleChange}
							handleSave={handleConfirmEdit}
							handleReset={() => {
								handleResetComment();
								setIsEditing(false);
							}}
							handleDelete={() => setShowDeleteConfirmation(true)}
						/>
					) : (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
							}}
						>
							{/* Action buttons row */}
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button
									size="small"
									variant="outlined"
									startIcon={<EditIcon />}
									onClick={() => setIsEditing(true)}
								>
									Edit
								</Button>
								<Button
									size="small"
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={() => setShowDeleteConfirmation(true)}
								>
									Delete
								</Button>
							</Box>

							{/* Mobile-only: Show additional info that's hidden in table */}
							<Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mb: 1 }}>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
									<Typography variant="caption" color="textSecondary">Client:</Typography>
									<Typography variant="body2">{comment.customerName}</Typography>
								</Box>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
									<Typography variant="caption" color="textSecondary">Case:</Typography>
									<Typography variant="body2">{comment.caseTitle}</Typography>
								</Box>
							</Box>
							<Card>
								<CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
									<Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
										Comment
									</Typography>
									{comment.description ? (
										<Box
											sx={{
												'& p': { m: 0 },
												'& img': { maxWidth: '100%', height: 'auto' },
												'& a': { color: 'primary.main' },
												typography: 'body2',
											}}
											dangerouslySetInnerHTML={{ __html: comment.description }}
										/>
									) : (
										<Typography variant="body2" color="text.secondary">
											No comment
										</Typography>
									)}
								</CardContent>
							</Card>
							<CommentAttachments
								commentId={comment.id}
								caseId={comment.caseID}
								customerId={comment.customerID}
							/>
						</Box>
					)}
				</Box>
			</TableCell>
			<DeleteCommentWarning
				open={showDeleteConfirmation}
				onClose={() => setShowDeleteConfirmation(false)}
				onConfirm={() => {
					setShowDeleteConfirmation(false);
					handleDeleteComment();
				}}
			/>
		</TableRow>
	);
};

export default CaseCommentsTableRowExpanded;
